// ###### IMPORTS
import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { saveBalanceSnapshotWithItems, previewSnapshotMarketInfo } from '../../viewServices/assets_api';
import {
  emptyRow,
  buildItemsPayloadFromRowsAllowEmptyLines,
  formatApiError,
} from '../../viewServices/snapshot_form_shared';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DatePicker from 'shared_items/DatePicker';
import FormTableSnapshotItems from './FormTableSnapshotItems';
import { buildTradeablePositionRowsAsOf } from '../../viewServices/tradeable_snapshot_positions';

const initialHeader = () => ({
  snapshot_date: '',
  is_closing: true,
  notes: '',
});

// ###### INLINE NEW SNAPSHOT — deposits + tradeables tables (Manage card)
const FormNewSnapshotInline = () => {
  const { assets, operations, refreshFull } = useAssetsData();
  const [header, setHeader] = useState(initialHeader);
  const [depositRows, setDepositRows] = useState([emptyRow()]);
  const [tradeableRows, setTradeableRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [prefillMarketBusy, setPrefillMarketBusy] = useState(false);

  const depositAssets = useMemo(
    () => (assets || []).filter((a) => a.asset_type === 'deposit'),
    [assets]
  );
  const tradeableAssets = useMemo(
    () => (assets || []).filter((a) => a.asset_type === 'tradeable'),
    [assets]
  );

  const resetForm = useCallback(() => {
    setHeader(initialHeader());
    setDepositRows([emptyRow()]);
    setTradeableRows([emptyRow()]);
  }, []);

  const clearDepositRows = useCallback(() => {
    setDepositRows([emptyRow()]);
  }, []);

  const clearTradeableRows = useCallback(() => {
    setTradeableRows([emptyRow()]);
  }, []);

  const preFillActiveDeposits = useCallback(() => {
    const matches = (assets || [])
      .filter((a) => a.asset_type === 'deposit' && a.is_active === true && a.id != null)
      .sort((a, b) => {
        const ca = a.asset_code != null ? String(a.asset_code).trim() : '';
        const cb = b.asset_code != null ? String(b.asset_code).trim() : '';
        return ca.localeCompare(cb, undefined, { sensitivity: 'base' });
      });

    if (matches.length === 0) {
      setDepositRows([emptyRow()]);
      toast('No active deposit assets to pre-fill.');
      return;
    }

    setDepositRows(
      matches.map((a) => ({
        ...emptyRow(),
        asset: String(a.id),
      }))
    );
    toast.success(`Pre-filled ${matches.length} row${matches.length === 1 ? '' : 's'}.`);
  }, [assets]);

  const preFillTradeablePositions = useCallback(() => {
    const d = header.snapshot_date?.trim();
    if (!d) {
      toast.error('Select a snapshot date before pre-filling positions.');
      return;
    }
    const { rows } = buildTradeablePositionRowsAsOf(d, tradeableAssets, operations);
    if (rows.length === 0) {
      setTradeableRows([emptyRow()]);
      toast('No non-zero tradeable positions as of this date.');
      return;
    }
    setTradeableRows(rows);
    toast.success(`Pre-filled ${rows.length} position row${rows.length === 1 ? '' : 's'}.`);
  }, [header.snapshot_date, tradeableAssets, operations]);

  const preFillTradeableMarketInfo = useCallback(async () => {
    const d = header.snapshot_date?.trim();
    if (!d) {
      toast.error('Select a snapshot date before pre-filling market info.');
      return;
    }
    const ids = [
      ...new Set(
        tradeableRows
          .map((r) => r.asset?.trim())
          .filter(Boolean)
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n))
      ),
    ];
    if (ids.length === 0) {
      toast('Select at least one tradeable asset to fetch market info.');
      return;
    }
    try {
      setPrefillMarketBusy(true);
      const { data } = await previewSnapshotMarketInfo({
        snapshot_date: d,
        asset_ids: ids,
      });
      const byAsset = new Map();
      (data.results || []).forEach((row) => {
        if (row.eligible && row.asset_id != null) {
          byAsset.set(Number(row.asset_id), row);
        }
      });
      const mergeCount = tradeableRows.reduce((acc, r) => {
        const raw = r.asset?.trim();
        if (!raw) return acc;
        const aid = Number(raw);
        if (Number.isNaN(aid)) return acc;
        const res = byAsset.get(aid);
        if (!res) return acc;
        const hasMuv = res.market_unitary_value != null && res.market_unitary_value !== '';
        const hasSrc = res.market_source != null;
        return acc + (hasMuv || hasSrc ? 1 : 0);
      }, 0);
      setTradeableRows((prev) =>
        prev.map((r) => {
          const raw = r.asset?.trim();
          if (!raw) return r;
          const aid = Number(raw);
          if (Number.isNaN(aid)) return r;
          const res = byAsset.get(aid);
          if (!res) return r;
          const next = { ...r };
          if (res.market_unitary_value != null && res.market_unitary_value !== '') {
            next.market_unitary_value = String(res.market_unitary_value);
          }
          if (res.market_source != null) {
            next.market_source_json =
              typeof res.market_source === 'string'
                ? res.market_source
                : JSON.stringify(res.market_source);
          }
          return next;
        })
      );
      const yahooErr = (data.yahoo_errors || []).length;
      if (mergeCount > 0) {
        toast.success(
          `Market info merged for ${mergeCount} row${mergeCount === 1 ? '' : 's'}.${yahooErr ? ` (${yahooErr} Yahoo warning(s))` : ''}`
        );
      } else if (yahooErr) {
        toast.error(`Market fetch completed with ${yahooErr} Yahoo issue(s); check market source on rows.`);
      } else {
        toast('No eligible Yahoo-backed assets in the selection (ticker / is_updated).');
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to pre-fill market info.');
    } finally {
      setPrefillMarketBusy(false);
    }
  }, [header.snapshot_date, tradeableRows]);

  const handleHeaderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHeader((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const updateDepositRow = (key, field, value) => {
    setDepositRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const addDepositRow = () => setDepositRows((prev) => [...prev, emptyRow()]);
  const removeDepositRow = (key) => {
    setDepositRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const updateTradeableRow = (key, field, value) => {
    setTradeableRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };
  const addTradeableRow = () => setTradeableRows((prev) => [...prev, emptyRow()]);
  const removeTradeableRow = (key) => {
    setTradeableRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!header.snapshot_date?.trim()) {
      toast.error('Select a snapshot date.');
      return;
    }

    const depositItems = buildItemsPayloadFromRowsAllowEmptyLines(
      depositRows,
      'Deposit assets',
      assets
    );
    if (depositItems === null) return;
    const tradeableItems = buildItemsPayloadFromRowsAllowEmptyLines(
      tradeableRows,
      'Tradeable assets',
      assets
    );
    if (tradeableItems === null) return;

    const depositIds = new Set(depositItems.map((it) => it.asset));
    for (const it of tradeableItems) {
      if (depositIds.has(it.asset)) {
        toast.error('The same asset cannot appear in both deposit and tradeable sections.');
        return;
      }
    }

    const combined = [...depositItems, ...tradeableItems];
    if (combined.length < 1) {
      toast.error('Add at least one valid line in deposits and/or tradeables.');
      return;
    }

    try {
      setSubmitting(true);
      await saveBalanceSnapshotWithItems({
        snapshot_date: header.snapshot_date,
        is_closing: !!header.is_closing,
        notes: header.notes?.trim() || '',
        items: combined,
      });
      toast.success('Snapshot created.');
      resetForm();
      if (refreshFull) await refreshFull();
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to save.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {(submitting || prefillMarketBusy) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/90">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
            <SpinnerLoad />
          </div>
        </div>
      )}

      <h3 className="mb-3 text-sm font-semibold text-gray-900">New snapshot</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-0.5 block text-xs text-gray-500">Snapshot date</label>
          <div className="flex flex-wrap items-center gap-4">
            <DatePicker
              name="snapshot_date"
              value={header.snapshot_date}
              onChange={handleHeaderChange}
              mode="single"
              colorScheme="green"
              disabled={submitting || prefillMarketBusy}
              placeholder="Select date"
              className="min-h-[2.25rem] w-48 shrink-0 justify-between"
            />
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                name="is_closing"
                checked={header.is_closing}
                onChange={handleHeaderChange}
                disabled={submitting || prefillMarketBusy}
                className="rounded border-gray-300 text-green-700 focus:ring-green-600"
              />
              Closing snapshot
            </label>
          </div>
        </div>

        <div>
          <label className="mb-0.5 block text-xs text-gray-500">Notes</label>
          <textarea
            name="notes"
            value={header.notes}
            onChange={handleHeaderChange}
            rows={2}
            disabled={submitting || prefillMarketBusy}
            className="min-h-[2.5rem] w-full resize-y rounded-md border px-2 py-1.5 text-sm"
            placeholder="Optional"
          />
        </div>

        <FormTableSnapshotItems
          title="Items | Deposit Assets"
          rows={depositRows}
          updateRow={updateDepositRow}
          addRow={addDepositRow}
          removeRow={removeDepositRow}
          assets={depositAssets}
          submitting={submitting || prefillMarketBusy}
          toolbarActions={
            <>
              <button
                type="button"
                onClick={preFillActiveDeposits}
                disabled={submitting || prefillMarketBusy}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Pre-fill
              </button>
              <button
                type="button"
                onClick={clearDepositRows}
                disabled={submitting || prefillMarketBusy}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear
              </button>
            </>
          }
        />

        <FormTableSnapshotItems
          title="Items | Tradeable Assets"
          rows={tradeableRows}
          updateRow={updateTradeableRow}
          addRow={addTradeableRow}
          removeRow={removeTradeableRow}
          assets={tradeableAssets}
          submitting={submitting || prefillMarketBusy}
          toolbarActions={
            <>
              <button
                type="button"
                onClick={preFillTradeablePositions}
                disabled={submitting || prefillMarketBusy}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Pre-fill positions
              </button>
              <button
                type="button"
                onClick={preFillTradeableMarketInfo}
                disabled={submitting || prefillMarketBusy}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Pre-fill market info
              </button>
              <button
                type="button"
                onClick={clearTradeableRows}
                disabled={submitting || prefillMarketBusy}
                className="inline-flex items-center rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear
              </button>
            </>
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-gray-100 pt-3">
          <p className="min-w-0 flex-1 text-left text-xs text-gray-500">
            At least one line with an asset and value is required.
          </p>
          <button
            type="submit"
            disabled={submitting || prefillMarketBusy}
            className={`shrink-0 rounded-xl px-5 py-2 text-sm font-medium text-white ${
              submitting ? 'cursor-not-allowed bg-green-400' : 'bg-green-700 hover:bg-green-800'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormNewSnapshotInline;
